import React from 'react';
import { Container, Typography, Box, Paper, Divider, Stack } from '@mui/material';

export default function PrivacyPolicy() {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Last Updated: April 4, 2026
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>1. Introduction</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Welcome to Questguru (operated by Arithmetic Infotech). We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you as to how we look after your personal data when you visit our website and sign up for our services.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>2. What Personal Data We Collect</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We collect only the minimum personal data necessary to provide our educational services. Categories of data we may collect include:
            </Typography>
            <Typography component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
              <li><strong>Account Data:</strong> name, username, email address (used for sign-in, notifications, and account recovery).</li>
              <li><strong>Usage &amp; Learning Data:</strong> progress, answers, scores, quizzes attempted and interactions with study material (used to personalize learning).</li>
              <li><strong>Technical Data:</strong> device type, browser, IP address, and basic diagnostics to secure and operate the service.</li>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We do not require or collect more sensitive information (such as government ID or financial data) to use our basic educational features.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>3. Children and Parental Consent</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Our service is intended for students. We do not knowingly collect personal information from children under 13 without verifiable parental consent. If you are a parent or guardian and believe your child under 13 has created an account without consent, contact us immediately and we will delete the child’s account and related data.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>4. How We Use and Share Data</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We use data to deliver the service, secure accounts, and improve learning outcomes. We will not sell your personal data. We may share limited data with trusted third parties for the following purposes only:
            </Typography>
            <Typography component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
              <li>Authentication and identity providers (e.g., Firebase) to manage accounts.</li>
              <li>Analytics providers for aggregated, anonymized usage metrics.</li>
              <li>Payment processors if you purchase paid features (if applicable) — only when you choose to pay.</li>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All third-party partners are required to use data only for the purposes we specify and to maintain strong security controls.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>Exam Aspirants &amp; Study Data</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If you use Questguru for exam preparation, we use your interaction and performance data to personalize study plans and provide targeted feedback. Study and performance data are used to improve learning outcomes and are shared in aggregated or anonymized form only. We do not disclose personally identifiable study data to third parties in ways that would compromise your privacy or exam integrity.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Users must not upload or request assistance to share actual exam or copyrighted test materials that would violate exam rules; such actions are prohibited and may lead to account suspension.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>5. Camera &amp; Device Permissions</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The Questguru mobile app may request access to your device camera and photo library. Camera access is used solely to allow you to photograph handwritten answer sheets and question paper documents for scanning and upload. Photo library access is used to let you pick existing images or PDF documents for the same purpose. We do not use the camera or photo library for any other purpose, and camera/photo data is not shared with third parties except as necessary to process the document (e.g., uploading to our backend API for OCR).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>6. Cookies, Tracking &amp; Advertising</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We use cookies for essential functionality and to improve the service. The Questguru mobile app uses Google AdMob to display advertisements. Our website may display advertising via Google AdSense. Ad providers may collect device and usage data to serve relevant ads.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              On the web, we show an ads consent banner on first visit. You can accept or decline ads; if you decline, personalized ads will not be loaded on this device. You can change your choice by clearing site data or via your browser settings. For more information about Google's advertising policies and choices, see <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">Google's advertising policies</a>.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>6. Data Security and Storage</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We implement industry-standard security measures including TLS for data in transit and encrypted storage for sensitive information. Access to personal data is limited, logged, and reviewed. We regularly test and monitor our systems for vulnerabilities.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In the unlikely event of a data breach affecting personal information, we will notify affected users and relevant authorities promptly in accordance with applicable law.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>7. Data Retention, Access &amp; Deletion</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We retain personal data only as long as necessary to provide the service and as required by law. You may request access, correction, or deletion of your personal data. For requests concerning a child’s account, parents/guardians may request deletion or exercise rights on behalf of the child.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>8. Account Security</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Keep your account email and password secure. Enable two-factor authentication if available. Do not share your password. If you suspect unauthorized access, change your password and contact support immediately.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>9. International Transfers</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We may store and process information in servers located in different countries. When transferring data across borders we use appropriate safeguards to protect your information.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>10. Contact &amp; Complaints</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              For privacy questions, data requests, or to report concerns about a child’s account, contact us at: support@arithmeticinfotech.com. We will respond within a reasonable timeframe.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
